import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, sanitizeImageUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import DiscordIcon from './DiscordIcon';
import OnboardingModal from './OnboardingModal';
import StarCanvas from './StarCanvas';

const CONTACT_DISCORD = 'https://discord.gg/WNcXbE8VFP';

// ── Individual clan card – fetches its own badge ──────────────────────────────
function ClanCard({ clan, t }) {
  const { data: header } = useQuery({
    queryKey: ['lp_header', clan.slug],
    queryFn: () => apiGet(`/v1/clan/${clan.slug}/header`),
    staleTime: 600_000,
  });

  const badge = header?.badge ? sanitizeImageUrl(header.badge) : null;

  return (
    <Link to={`/clan/${clan.slug}`} className="lp-clan-card">
      <div className="lp-clan-badge-wrap">
        {badge
          ? <img src={badge} alt="" className="lp-clan-badge" />
          : <span className="lp-clan-badge-placeholder">🏰</span>
        }
      </div>
      <div className="lp-clan-info">
        <span className="lp-clan-name">{clan.display_name}</span>
        <span className="lp-clan-view-cta">{t('lp_view_dashboard')} →</span>
      </div>
    </Link>
  );
}

// ── Feature card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, accentColor, delay }) {
  return (
    <div className="lp-feature-card landing-fade-up" style={{ animationDelay: delay }}>
      <span className="lp-feature-icon" style={{ color: accentColor }}>{icon}</span>
      <h3 className="lp-feature-title">{title}</h3>
      <p className="lp-feature-desc">{desc}</p>
    </div>
  );
}

// ── Scrolling ticker ──────────────────────────────────────────────────────────
const TICKER_KEYS = [
  { icon: '⚔️', key: 'lp_ticker_war' },
  { icon: '📊', key: 'lp_ticker_season' },
  { icon: '🏆', key: 'lp_ticker_hof' },
  { icon: '🎯', key: 'lp_ticker_cwl' },
  { icon: '🛡️', key: 'lp_ticker_defence' },
  { icon: '📈', key: 'lp_ticker_historical' },
  { icon: '🔔', key: 'lp_ticker_reminders' },
  { icon: '🤝', key: 'lp_ticker_donations' },
  { icon: '⭐', key: 'lp_ticker_attack_stats' },
  { icon: '🏰', key: 'lp_ticker_clan_stats' },
  { icon: '💎', key: 'lp_ticker_cwl_bonus' },
  { icon: '📜', key: 'lp_ticker_event_feed' },
  { icon: '📋', key: 'lp_ticker_clan_rules' },
  { icon: '🌐', key: 'lp_ticker_clan_website' },
];

function Ticker({ t }) {
  // Triple the items for smoother infinite scroll
  const tripled = [...TICKER_KEYS, ...TICKER_KEYS, ...TICKER_KEYS];
  return (
    <div className="lp-ticker-bar">
      <div className="lp-ticker-inner">
        {tripled.map((item, i) => (
          <span key={i} className="lp-ticker-item">{item.icon} {t(item.key)}</span>
        ))}
      </div>
    </div>
  );
}

// ── Main landing page ─────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: '⚔️',
    titleKey: 'lp_feature_war_title',
    descKey: 'lp_feature_war_desc',
    accentColor: '#fbbf24',
    delay: '0.1s',
  },
  {
    icon: '📊',
    titleKey: 'lp_feature_season_title',
    descKey: 'lp_feature_season_desc',
    accentColor: '#60a5fa',
    delay: '0.2s',
  },
  {
    icon: '🏆',
    titleKey: 'lp_feature_hof_title',
    descKey: 'lp_feature_hof_desc',
    accentColor: '#a78bfa',
    delay: '0.3s',
  },
  {
    icon: '🔔',
    titleKey: 'lp_feature_discord_title',
    descKey: 'lp_feature_discord_desc',
    accentColor: '#5865f2',
    delay: '0.4s',
  },
  {
    icon: '👤',
    titleKey: 'lp_feature_profile_title',
    descKey: 'lp_feature_profile_desc',
    accentColor: '#34d399',
    delay: '0.5s',
  },
];

const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const LoginIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

export default function LandingPage() {
  const { data: clansData, isLoading } = useQuery({
    queryKey: ['lp_clans'],
    queryFn: () => apiGet('/v1/clans'),
    staleTime: 300_000,
  });

  const { user, isAuthenticated, isAdmin, isOwner, isMember, loginWithDiscord, logout, loading: authLoading, userProfile, profileLoaded, session, refreshProfile } = useAuth();
  const { t, lang, setLang } = useTranslation();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [modalMode, setModalMode] = useState('post-login');
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const menuRef = useRef(null);
  const langRef = useRef(null);
  const pendingProcessedRef = useRef(false);
  // Once a registration is submitted this session, suppress the auto-pop modal
  // regardless of what userProfile.status says (profile may lag behind)
  const registrationSubmittedRef = useRef(false);

  // Flow A: auto-pop registration modal after sign-in when account has no clan registration
  // Suppressed if a registration was just submitted this session or is pending in sessionStorage
  useEffect(() => {
    if (isAuthenticated && !authLoading && profileLoaded && userProfile && (userProfile.status === 'none' || userProfile.status === 'rejected')) {
      if (registrationSubmittedRef.current) return;
      if (sessionStorage.getItem('pending_registration')) return;
      setModalMode('post-login');
      setShowOnboarding(true);
    }
  }, [isAuthenticated, authLoading, profileLoaded, userProfile]);

  // Flow B: after OAuth redirect, submit pending registration if stored
  useEffect(() => {
    // Prevent duplicate processing
    if (pendingProcessedRef.current) return;

    const pending = sessionStorage.getItem('pending_registration');
    if (pending && isAuthenticated && session?.access_token) {
      pendingProcessedRef.current = true;
      registrationSubmittedRef.current = true;
      sessionStorage.removeItem('pending_registration');
      const { playerTag, clanTag, message } = JSON.parse(pending);
      import('../utils/api').then(({ apiPost }) => {
        apiPost('/v1/auth/register', { player_tag: playerTag, clan_tag: clanTag, message }, { withAuth: true })
          .then(() => refreshProfile())
          .catch(() => {});
      });
    }
  }, [isAuthenticated, session?.access_token]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogin = async () => { try { await loginWithDiscord(); } catch {} };
  const handleLogout = async () => { try { await logout(); setMenuOpen(false); } catch {} };
  const handleOnboardingComplete = useCallback(() => {
    registrationSubmittedRef.current = true;
    setShowOnboarding(false);
  }, []);
  // Request Access = pre-login mode (no Discord needed yet)
  const handleRequestAccess = () => {
    setModalMode('pre-login');
    setShowOnboarding(true);
  };

  const clans = Array.isArray(clansData) ? clansData : [];
  const firstClanSlug = clans[0]?.slug || null;
  const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || '';
  const userAvatar = user?.user_metadata?.avatar_url;

  return (
    <div className="lp-root">
      {showOnboarding && (
        <OnboardingModal
          clans={clans}
          onComplete={handleOnboardingComplete}
          onClose={() => setShowOnboarding(false)}
          mode={modalMode}
        />
      )}

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        {/* Animated background */}
        <div className="lp-hero-bg" aria-hidden="true">
          <StarCanvas />
          <div className="lp-blob lp-blob-1" />
          <div className="lp-blob lp-blob-2" />
          <div className="lp-blob lp-blob-3" />
        </div>

        {/* Nav bar */}
        <nav className="lp-nav">
          <div className="lp-nav-logo">
            <img
              src="/static/img/branding/Clansight.png"
              alt="Clansight"
              className="lp-nav-logo-img"
              style={{ height: '192px', width: 'auto' }}
            />
          </div>
          <div className="lp-nav-right">
            <div className="lp-lang-switcher" ref={langRef}>
              <button
                onClick={() => setLangOpen(o => !o)}
                className="lp-lang-btn"
              >
                <span className="lp-lang-label">{LANGS.find(l => l.code === lang)?.label || lang.toUpperCase()}</span>
                <span className="lp-lang-caret">{langOpen ? '▲' : '▼'}</span>
              </button>
              {langOpen && (
                <div className="lp-lang-menu">
                  {LANGS.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`lp-lang-option${l.code === lang ? ' active' : ''}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {authLoading ? null : !isAuthenticated ? (
              <button onClick={handleLogin} className="lp-nav-discord" style={{ cursor: 'pointer', background: 'rgba(88,101,242,0.15)', border: '1px solid rgba(88,101,242,0.45)' }}>
                <LoginIcon />
                <span>{t('lp_login')}</span>
              </button>
            ) : (
              <div className="lp-nav-user" ref={menuRef}>
                <button onClick={() => setMenuOpen(o => !o)} className="lp-nav-user-btn">
                  {userAvatar
                    ? <img src={userAvatar} alt={userName} className="lp-nav-avatar" />
                    : <span className="lp-nav-avatar-fallback">👤</span>
                  }
                  <span className="lp-nav-username">{userName}</span>
                  <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', marginLeft: '0.1rem' }}>{menuOpen ? '▲' : '▼'}</span>
                </button>
                {menuOpen && (
                  <div className="lp-nav-user-menu">
                    <div className="lp-nav-user-menu-header">
                      {userAvatar
                        ? <img src={userAvatar} alt={userName} className="lp-nav-avatar-lg" />
                        : <span className="lp-nav-avatar-fallback-lg">👤</span>
                      }
                      <div>
                        <div className="lp-nav-user-name">{userName}</div>
                        <div className="lp-nav-user-role">
                          {userProfile?.status === 'approved'
                            ? (userProfile?.memberships && userProfile.memberships.length > 0
                                ? userProfile.memberships.map(m => m.clan_name).join(', ')
                                : '✅ ' + t('role_approved'))
                            : userProfile?.status === 'pending' ? '⏳ ' + t('role_pending')
                            : '❌ ' + t('lp_not_registered')}
                        </div>
                      </div>
                    </div>
                    {!userProfile?.status || userProfile?.status === 'rejected' ? (
                      <button onClick={() => { setMenuOpen(false); setModalMode('post-login'); setShowOnboarding(true); }} className="lp-nav-register-btn">{t('lp_register_now')}</button>
                    ) : null}
                    {isMember && (() => {
                      const userClanSlug = userProfile?.memberships?.[0]?.slug
                        || clans[0]?.slug;
                      return userClanSlug ? (
                        <Link
                          to={`/clan/${userClanSlug}`}
                          onClick={() => setMenuOpen(false)}
                          className="lp-nav-settings-btn"
                        >
                          <span>📊</span>
                          <span>{t('lp_open_dashboard')}</span>
                        </Link>
                      ) : null;
                    })()}
                    {isOwner && (
                      <Link
                        to="/manage"
                        onClick={() => setMenuOpen(false)}
                        className="lp-nav-settings-btn"
                      >
                        <span>🏰</span>
                        <span>Manage Clans</span>
                      </Link>
                    )}
                    {isAdmin && (() => {
                      // Use user's registered clan, not just first global clan
                      const userClanSlug = userProfile?.memberships?.[0]?.slug || clans[0]?.slug;
                      return userClanSlug ? (
                        <Link
                          to={`/settings/${userClanSlug}`}
                          onClick={() => setMenuOpen(false)}
                          className="lp-nav-settings-btn"
                        >
                          <span>⚙️</span>
                          <span>Settings</span>
                        </Link>
                      ) : null;
                    })()}
                    <button onClick={handleLogout} className="lp-nav-logout-btn">{t('lp_sign_out')}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Hero content */}
        <div className="lp-hero-content">
          <div className="lp-hero-eyebrow landing-fade-up" style={{ animationDelay: '0s' }}>
            🏰 {t('lp_eyebrow')}
          </div>

          <h1 className="lp-hero-title landing-fade-up" style={{ animationDelay: '0.12s' }}>
            {t('lp_hero_title_1')}<br />
            <span className="lp-gradient-text">{t('lp_hero_title_2')}</span>
          </h1>

          <p className="lp-hero-sub landing-fade-up" style={{ animationDelay: '0.24s' }}>
            {t('lp_hero_sub')}
          </p>

          <div className="lp-hero-actions landing-fade-up" style={{ animationDelay: '0.36s' }}>
            <a href="#clans" className="lp-btn-gold">
              {t('lp_btn_explore')}
            </a>
            {/* Only show Request Access if not registered (no pending or approved status) */}
            {(!isAuthenticated || !userProfile?.status || userProfile?.status === 'none' || userProfile?.status === 'rejected') && (
              <button
                onClick={handleRequestAccess}
                className="lp-btn-access"
              >
                {t('lp_btn_access')}
              </button>
            )}
          </div>
        </div>

        {/* Scroll hint */}
        <div className="lp-scroll-hint" aria-hidden="true">↓</div>
      </section>

      {/* ── Ticker ───────────────────────────────────────────────────────── */}
      <Ticker t={t} />

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section className="lp-features-section">
        <div className="lp-section-wrap">
          <p className="lp-section-label">{t('lp_features_label')}</p>
          <h2 className="lp-section-title">{t('lp_features_title')}</h2>
          <div className="lp-features-grid">
            {FEATURES.map(f => (
              <FeatureCard key={f.titleKey} {...f} title={t(f.titleKey)} desc={t(f.descKey)} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Active Clans ─────────────────────────────────────────────────── */}
      <section id="clans" className="lp-clans-section">
        <div className="lp-section-wrap">
          <p className="lp-section-label">{t('lp_clans_label')}</p>
          <h2 className="lp-section-title">{t('lp_clans_title')}</h2>

          {isLoading ? (
            <div className="lp-clans-grid">
              {[1, 2, 3].map(i => (
                <div key={i} className="lp-clan-card-skeleton" />
              ))}
            </div>
          ) : clans.length === 0 ? (
            <p className="lp-empty-msg">{t('lp_clans_empty')}</p>
          ) : (
            <div className="lp-clans-grid">
              {clans.map(clan => (
                <ClanCard key={clan.slug} clan={clan} t={t} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── How to join ──────────────────────────────────────────────────── */}
      <section className="lp-join-section">
        <div className="lp-join-inner">
          <div className="lp-join-icon-wrap" aria-hidden="true">💬</div>
          <h2 className="lp-join-title">{t('lp_join_title')}</h2>
          <p className="lp-join-desc">
            {t('lp_join_desc')}
          </p>
          {CONTACT_DISCORD ? (
            <a
              href={CONTACT_DISCORD}
              target="_blank"
              rel="noopener noreferrer"
              className="lp-join-btn"
            >
              <DiscordIcon style={{ width: '1.25rem', height: '1.25rem', flexShrink: 0 }} />
              {t('lp_join_btn')}
            </a>
          ) : (
            <p className="lp-join-fallback">
              {t('lp_join_fallback')}
            </p>
          )}
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <span>Clansight</span>
        <span className="lp-footer-dot" aria-hidden="true">·</span>
        <span>{t('lp_footer_disclaimer')}</span>
        <span className="lp-footer-dot" aria-hidden="true">·</span>
        <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">
          {lang === 'de' ? 'Datenschutz' : 'Privacy'}
        </Link>
        <span className="lp-footer-dot" aria-hidden="true">·</span>
        <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">
          {lang === 'de' ? 'Nutzungsbedingungen' : 'Terms'}
        </Link>
        <span className="lp-footer-dot" aria-hidden="true">·</span>
        <Link to="/imprint" className="text-blue-400 hover:text-blue-300 underline">
          {lang === 'de' ? 'Impressum' : 'Imprint'}
        </Link>
      </footer>

    </div>
  );
}
